<template>
    <div class="px-4"> 
        <ul class="nav nav-tabs nav-tabs-v2" role="tablist">
            <li class="nav-item" role="presentation">
                <a 
                    class="nav-link" 
                    :class="{ 'active': activeTab === 'tudo' }" 
                    href="#" 
                    @click.prevent="selectTab('tudo')">
                    <span class="tab-label">Tudo</span>
                    <span v-if="counts.total > 0" class="tab-badge">{{ counts.total }}</span>
                </a>
            </li>
            <li class="nav-item" role="presentation">
                <a 
                    class="nav-link" 
                    :class="{ 'active': activeTab === 'atendimentos' }" 
                    href="#" 
                    @click.prevent="selectTab('atendimentos')">
                    <span class="tab-label">Atendimentos</span>
                    <span v-if="counts.atendimentos > 0" class="tab-badge">{{ counts.atendimentos }}</span>
                </a>
            </li>
            <li  class="nav-item" role="presentation">
                <a 
                    class="nav-link" 
                    :class="{ 'active': activeTab === 'grupos' }" 
                    href="#" 
                    @click.prevent="selectTab('grupos')">
                    <span class="tab-label">Grupos</span>
                    <span v-if="counts.grupos > 0" class="tab-badge">{{ counts.grupos }}</span>
                </a>
            </li>
            <li class="nav-item d-none" role="presentation">
                <a 
                    class="nav-link" 
                    :class="{ 'active': activeTab === 'favoritos' }" 
                    href="#" 
                    @click.prevent="selectTab('favoritos')">
                    <i class="ri-star-line"></i> Favoritos
                </a>
            </li>
        </ul>
    </div>
</template>

<script setup>
import { computed, watch } from 'vue'; 
import { useChatStore } from '@/stores/app/chatStore';
import { useUiStore } from '@/stores/app/uiStore';
import { MakeChat } from '@/stores/app/chatUtils'; 

const props = defineProps({
    activeTab: {
        type: String,
        default: 'tudo'
    },
    searchQuery: {
        type: String,
        default: ''
    }
});
const emit = defineEmits(['update:activeTab']);
const chatStore = useChatStore();
const uiStore = useUiStore();

watch(() => props.activeTab, (newTab) => {
    uiStore.setActiveListTab(newTab);
}, { immediate: true });

const counts = computed(() => {
    /* Atendimento conduzido por Agente de IA não aparece nesta lista: `ListUser`
       o separa em `AIChatList`, exibido só na aba "Atendimentos por IA" do menu
       lateral. Contar aqui o que não está renderizado abaixo fazia a badge de
       "Tudo"/"Atendimentos" divergir das linhas visíveis. O predicado vem do
       getter `chatEhConduzidoPorIa`, o mesmo que monta `AIChatList` — quando a
       condição morava copiada em cada componente, ChatHeader e ChatInput já
       divergiram entre si. */
    const baseList = (chatStore.filteredChats || [])
        .filter(c => !chatStore.chatEhConduzidoPorIa(c));
    /* Mesmo predicado de BUSCA da lista (`MakeChat.chatCorrespondeABusca`), pelo motivo
       que o comentário acima descreve: a normalização daqui ignorava separadores e só
       olhava `name`, então divergia — e não encontrava grupo pelo nome da comunidade.
       Só a busca é compartilhada: os filtros de status ("admin", "em espera") são
       aplicados pelo `ListUser` e não por `filteredChats`, então com um deles ligado a
       contagem segue podendo divergir das linhas. Divergência anterior a isto. */
    const sq = MakeChat.normalizarBusca(props.searchQuery || '');

    const list = sq
        ? baseList.filter(c => MakeChat.chatCorrespondeABusca(c, sq))
        : baseList;

    const grupos = list.filter(c => c.isGroup).length;
    const atendimentos = list.filter(c => !c.isGroup).length;

    return {
        total: list.length,
        grupos,
        atendimentos
    };
});

function selectTab(tabName) {
    emit('update:activeTab', tabName);
    uiStore.setActiveListTab(tabName); // Garante atualização imediata no clique
}
</script>

<style scoped>

.nav-item{
    font-size: 0.789rem !important;
}
/* `.nav-tabs` do Bootstrap vem com `flex-wrap: wrap`: quando a badge passava a
   3 dígitos a soma das tabs estourava a largura da coluna e a última caía para
   a linha de baixo. `nowrap` impede a quebra; a rolagem horizontal (sem barra
   visível) é a válvula de escape para o caso extremo — melhor que espremer os
   rótulos. */
.nav-tabs-v2 {
    border-bottom: none;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.nav-tabs-v2::-webkit-scrollbar {
    display: none;
}

.nav-tabs-v2 .nav-link {
    border: none;
    border-bottom: 3px solid transparent;
    color: var(--bs-text-muted);
    padding: 10px 4px;
    margin-right: 12px;
    font-weight: 600;
    text-decoration: none;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 5px;
}

/* Sem `flex-shrink: 0` o algoritmo flex comprime a tab e o texto quebra dentro
   dela ("Atendi- mentos") em vez de a tab manter sua largura natural. */
.nav-tabs-v2 .nav-item {
    flex: 0 0 auto;
}

.tab-label {
    white-space: nowrap;
}

.nav-tabs-v2 .nav-link i {
    font-size: 14px;
    vertical-align: middle;
    margin-top: -2px;
    margin-right: 2px;
}

.nav-tabs-v2 .nav-link.active,
.nav-tabs-v2 .nav-link:hover {
    color: var(--bs-success);
    border-bottom-color: var(--bs-success);
    background-color: transparent;
}

/* Pílula que cresce só na horizontal: `min-width` mantém o círculo com 1 dígito
   e o padding lateral assume a forma de pílula a partir de 2-3 dígitos, em vez
   de virar um círculo achatado. `border-radius` alto garante a curva em
   qualquer largura; `tabular-nums` evita a badge "pulsar" de largura a cada
   atualização do contador. */
.tab-badge {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    background-color: #f0f2f5;
    color: #6c757d;
    font-size: 10px;
    line-height: 1.5;
    padding: 1px 7px;
    border-radius: 999px;
    font-weight: 700;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    transition: background-color 0.2s, color 0.2s;
}

[data-bs-theme="dark"] .tab-badge {
    background-color: #f0f2f514;
    color: #70777d;
}

.nav-tabs-v2 .nav-link.active .tab-badge {
    background-color: rgba(var(--bs-success-rgb), 0.15); /* Fundo verde bem claro */
    color: var(--bs-success); /* Texto verde */
}
</style>